import React, { createContext, useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TenantContext = createContext(null);

/**
 * Detecta se o background é "claro" (precisa de texto escuro).
 * Aceita hex sólido ou linear-gradient (extrai a primeira cor do gradiente).
 */
function isLightBg(value) {
  if (!value || typeof value !== 'string') return true;
  const m = value.match(/#([0-9a-fA-F]{6})/);
  if (!m) return true;
  const hex = m[1];
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  // YIQ luminance
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 150;
}

// Paleta padrão Nexus, igual para todos os clientes. As cores gravadas por
// cliente (corPrimaria/corSidebar/corCards) continuam no banco mas são ignoradas;
// para reativar a personalização no futuro, remova o override em `branding`.
const STANDARD_COLORS = {
  corPrimaria: '#6366F1',
  corSidebar: '#ffffff',
  corCards: '#6366F1',
};

const DEFAULT_BRANDING = {
  nomeDoSistema: 'Gastify',
  ...STANDARD_COLORS,
  logo: '',
  nomeEmpresa: 'Cada gasto, uma decisão consciente.',
};

export const TenantProvider = ({ children }) => {
  const { tenantSlug } = useParams();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!tenantSlug) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    axios.get(`${API_URL}/api/tenants/${tenantSlug}/branding`)
      .then(res => setTenant(res.data))
      .catch(err => {
        const status = err.response?.status;
        if (status === 404) setError('not_found');
        else if (status === 403) setError('suspended');
        else setError('unknown');
      })
      .finally(() => setLoading(false));
  }, [tenantSlug]);

  // Apply branding to CSS variables
  useEffect(() => {
    const cor = STANDARD_COLORS.corPrimaria;
    const sidebar = STANDARD_COLORS.corSidebar;
    const cards = STANDARD_COLORS.corCards;
    const root = document.documentElement;
    root.style.setProperty('--fin-blue', cor);
    root.style.setProperty('--tenant-primary', cor);
    root.style.setProperty('--tenant-sidebar', sidebar);
    root.style.setProperty('--tenant-cards', cards);
    // For text contrast on sidebar (decide light/dark text)
    root.style.setProperty('--tenant-sidebar-fg', isLightBg(sidebar) ? '#0f172a' : '#f8fafc');
  }, [tenant]);

  const branding = { ...DEFAULT_BRANDING, ...(tenant || {}), ...STANDARD_COLORS, slug: tenantSlug };

  return (
    <TenantContext.Provider value={{ tenant: branding, loading, error, slug: tenantSlug }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => useContext(TenantContext) || { tenant: DEFAULT_BRANDING, loading: false, error: null };
